import {
    Stack,
    Text,
    Loader,
    Title,
    Group as MGroup,
    ActionIcon,
} from "@mantine/core";
import IconDotsVertical from "../../assets/dots-vertical.svg?react";
import IconSearch from "../../assets/search.svg?react";
import {spotlight} from "@mantine/spotlight";
import classes from "./ChatSearchHistoryPage.module.css";
import {useTranslation} from "react-i18next";
import {isToday, subDays} from "date-fns";
import {useMemo} from "react";
import {ExperimentalLink} from "../../components/ExperimentalLink";
import {chatApi} from "../chat/chatApi";
import type {ThreadInfo} from "../../types";
import {USER_THREADS_PAGE_SIZE} from "../../types";

type Group = {
    label: string;
    items: ThreadInfo[];
    showLabel: boolean;
};

export function ChatSearchHistoryPage() {
    const {data, isLoading} = chatApi.endpoints.getUserThreads.useQuery({
        page: 0,
        pageSize: USER_THREADS_PAGE_SIZE,
    });
    const {t} = useTranslation();

    const groups = useMemo(() => {
        const items = [...(data?.items ?? [])];
        if (items.length === 0) return [];

        items.sort((a, b) => {
            const da = a.lastUpdated ? new Date(a.lastUpdated).getTime() : 0;
            const db = b.lastUpdated ? new Date(b.lastUpdated).getTime() : 0;
            return db - da;
        });

        const sevenDaysAgo = subDays(new Date(), 7);
        const today: ThreadInfo[] = [];
        const lastWeek: ThreadInfo[] = [];
        const previous: ThreadInfo[] = [];

        for (const item of items) {
            const date = item.lastUpdated ? new Date(item.lastUpdated) : null;
            if (date && isToday(date)) {
                today.push(item);
            } else if (date && date >= sevenDaysAgo) {
                lastWeek.push(item);
            } else {
                previous.push(item);
            }
        }

        const hasRecentGroup = today.length > 0 || lastWeek.length > 0;
        const result: Group[] = [];
        if (today.length > 0)
            result.push({
                label: t("date.today"),
                items: today,
                showLabel: true,
            });
        if (lastWeek.length > 0)
            result.push({
                label: t("date.lastWeek"),
                items: lastWeek,
                showLabel: true,
            });
        if (previous.length > 0)
            result.push({
                label: t("date.previously"),
                items: previous,
                showLabel: hasRecentGroup,
            });
        return result;
    }, [data?.items, t]);

    if (isLoading) {
        return (
            <Stack align="center">
                <Loader />
            </Stack>
        );
    }

    if (groups.length === 0) {
        return (
            <Stack>
                <Text c="dimmed">No chat threads found.</Text>
            </Stack>
        );
    }

    return (
        <Stack gap={0}>
            <MGroup justify="space-between" align="center" mb="md">
                <Title order={3} size={"lg"} className={classes.title}>
                    {t("common.recentThreads")}
                </Title>
                <ActionIcon
                    variant="subtle"
                    color="gray"
                    onClick={() => spotlight.open()}
                >
                    <IconSearch width={18} height={18} />
                </ActionIcon>
            </MGroup>
            {groups.map((group) => (
                <Stack key={group.label} gap={0} className={classes.groupStack}>
                    {group.showLabel && (
                        <Text className={classes.groupLabel}>
                            {group.label}
                        </Text>
                    )}
                    {group.items.map((item, i) => (
                        <MGroup
                            justify="space-between"
                            wrap="nowrap"
                            className={classes.itemRow}
                            key={`chatSearch-${item.threadId ?? i}`}
                        >
                            <ExperimentalLink
                                href={`/chat/${item.threadId}`}
                                className={classes.itemLink}
                            >
                                <Text className={classes.itemName}>
                                    {item.name}
                                </Text>
                            </ExperimentalLink>
                            <ActionIcon variant="subtle" color="gray" size="sm">
                                <IconDotsVertical width={16} height={16} />
                            </ActionIcon>
                        </MGroup>
                    ))}
                </Stack>
            ))}
        </Stack>
    );
}
